// Tashkiliy daraxt — `xsib-platform` `org` moduli bilan kontrakt.
//
//   GET    /api/org-nodes?q&kind&state&page&size  → {rows, total, page, size}
//   GET    /api/org-nodes/{id}                    → OrgNodeView
//   GET    /api/org-nodes/{id}/children           → OrgNodeView[]
//   GET    /api/org-nodes/{id}/subtree            → OrgNodeView[]  (path bo'yicha)
//   POST   /api/org-nodes                         → OrgNodeView (201)
//   PUT    /api/org-nodes/{id}                    → OrgNodeView
//   POST   /api/org-nodes/{id}/parent  {parentId} → OrgNodeView   (KO'CHIRISH)
//   POST   /api/org-nodes/{id}/state   {state}    → OperationResult
//
//   GET    /api/org-node-types                    → OrgNodeTypeView[]
//   POST   /api/org-node-types                    → OrgNodeTypeView (201)
//   PUT    /api/org-node-types/{id}               → OrgNodeTypeView
//   DELETE /api/org-node-types/{id}               → 204
//
// Huquqlar: o'qish (ikkalasi ham) `org.node.read`, tugun yozish
// `org.node.write`, TUR ta'rifi `org.type.write`.
//
// =====================================================================
// ⚠️ BU FAYLNING ENG MUHIM QOIDASI — QARORLAR.md §19.3
// =====================================================================
// Tur `enum` EMAS, JADVAL. Administrator panelda "Mintaqa" turini ochib,
// unga `costCenter = true` qo'yishi mumkin — va moliya uni darhol
// o'lchov sifatida ko'radi, bitta qator kod o'zgarmasdan.
//
// Bu FAQAT bitta shart bilan ishlaydi: kod tur NOMIGA emas,
// IMKONIYATIGA qaraydi.
//
//     if (node.kind === 'BRANCH')        ← TAQIQLANGAN
//     if (type.settlementParty)          ← TO'G'RI
//
// Buzilishning oqibati JIM: administrator yangi tur ochadi, panel uni
// jimgina e'tiborsiz qoldiradi — maydon chizilmaydi, tugma chiqmaydi,
// hech qanday xato ham bo'lmaydi. Backendda bu qoidani
// `OrgNodeTypeGuardTest` qo'riqlaydi; panelda qorovul yo'q, shuning
// uchun tur kodi bilan HECH QANDAY solishtirish yozilmaydi. Yagona
// ishlatilishi — `typeOf()` orqali turni topish kaliti.
//
// ⚠️ Chuqurlik CHEKLANMAGAN (§19.2). Bu yerdagi daraxt qurish rekursiv
// va "ikki qavat" degan faraz hech qayerda yo'q.
//
// ⚠️ `tenant` daraxtning tuguni EMAS (§19.1) — u daraxtdan yuqorida
// turgan izolyatsiya chegarasi. Shuning uchun javobda `tenantId` yo'q va
// panel uni so'ramaydi ham.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import type { Translate } from '@/shared/api/errors'
import { seededName } from '@/shared/lib/seededName'
import {
  ancestorChain as ancestorChainOf,
  buildTree,
  collectSubtreeIds as collectSubtreeIdsOf,
} from '@/shared/lib/tree'

/**
 * Tugun holati — `org_node_state_valid` ning nusxasi.
 *
 * ⚠️ Bu ro'yxat YOPIQ va `kind` bilan ZID EMAS (`OrgNodeStateChange`
 * javadoc'i): `kind` — administrator kengaytiradigan MA'LUMOT, holat esa
 * yopiq hayot davri. Uni kengaytirish bazadagi `CHECK` ni ham,
 * unga tayangan kodni ham o'zgartirishni talab qiladi.
 */
export const ORG_NODE_STATES = ['ACTIVE', 'SUSPENDED', 'CLOSED'] as const
export type OrgNodeState = (typeof ORG_NODE_STATES)[number]

/**
 * Hisob-kitob usuli — `org_node_mode_valid`.
 *
 * ⚠️ `features/agent/agentApi.ts` da ham shu nomli ro'yxat bor va bu
 * TAKROR EMAS, ATAYLAB: har modul o'z API kontraktini o'zi e'lon qiladi
 * (`SITE_STATES`, `AGENT_STATES` bilan bir xil andoza) va `features/*`
 * bir-birini import qilmaydi (07-FRONTEND.md §6). Bugun ikkala endpoint
 * ham bitta jadvalga boradi (`agent.party_node` — `platform.org_node`
 * ustidagi moslik VIEW i), lekin ular ikki AYRIM shartnoma va §20.9-A
 * ning 3-bosqichida biri o'chadi. Haqiqiy takror — tarjima matnlari
 * bo'lardi, ular esa BIR JOYDA: `settlementMode.*` kalitlari.
 */
export const SETTLEMENT_MODES = ['DEPOSIT', 'POSTPAID'] as const
export type SettlementMode = (typeof SETTLEMENT_MODES)[number]

/**
 * Turning IMKONIYAT bayroqlari — §19.3 ning butun mohiyati.
 *
 * Panel har savolga NOM bilan emas, shu bayroqlar bilan javob beradi:
 *   "bu tugunga qarz limiti maydonini chizaymi?"  → settlementParty
 *   "ombor qoldig'ini ko'rsataymi?"               → holdsStock
 *   "xarajat markazimi?"                          → costCenter
 *   "xodim biriktirilishi mumkinmi?"              → hasEmployees
 *
 * Ro'yxat sifatida ham kerak: forma va jadval ularni BIR MARTA yozilgan
 * tartibda aylanib chiqadi, ya'ni yangi bayroq qo'shilganda uni ikki
 * joyda qo'lda qo'shish shart emas.
 */
export const ORG_CAPABILITIES = [
  'costCenter',
  'holdsStock',
  'settlementParty',
  'hasEmployees',
] as const
export type OrgCapability = (typeof ORG_CAPABILITIES)[number]

// --- Tugun ------------------------------------------------------------------

const orgNodeSchema = z.object({
  id: uuidSchema,
  parentId: uuidSchema.nullish(),
  /** Materiallashgan `ltree` yo'li, matn ko'rinishida */
  path: z.string(),
  /** `nlevel(path)` — BAZADAN keladi, nuqtalarni sanab emas */
  depth: z.number(),
  code: z.string(),
  displayName: z.string(),
  /** `org_node_type.code` — turlar ro'yxati bilan bog'lanadi */
  kind: z.string(),
  settlementMode: z.enum(SETTLEMENT_MODES).nullish(),
  state: z.enum(ORG_NODE_STATES),
  /** MINOR birlikda; `null` = limit qo'yilmagan (nol limitdan boshqa ma'no) */
  debtLimit: z.number().nullish(),
  managerUserId: uuidSchema.nullish(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
})

export interface OrgNode {
  id: string
  parentId: string | null
  path: string
  depth: number
  code: string
  displayName: string
  kind: string
  settlementMode: SettlementMode | null
  state: OrgNodeState
  debtLimit: number | null
  /** `identity` foydalanuvchisi — murojaat o'z vaqtida qabul qilinmasa xabar shu odamga boradi */
  managerUserId: string | null
  createdAt: string | null
  updatedAt: string | null
}

function toOrgNode(raw: z.infer<typeof orgNodeSchema>): OrgNode {
  return {
    id: raw.id,
    parentId: raw.parentId ?? null,
    path: raw.path,
    depth: raw.depth,
    code: raw.code,
    displayName: raw.displayName,
    kind: raw.kind,
    settlementMode: raw.settlementMode ?? null,
    state: raw.state,
    // ⚠️ `?? 0` QO'YILMAYDI: "limit qo'yilmagan" va "limit nol" boshqa
    // narsa (docs/02-LEDGER.md §8) va backend ham ularni ajratadi.
    debtLimit: raw.debtLimit ?? null,
    managerUserId: raw.managerUserId ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  }
}

const orgNodePageSchema = z.object({
  rows: z.array(orgNodeSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

/**
 * Server bir so'rovda beradigan eng katta sahifa — `PageBounds.MAX_SIZE`.
 *
 * ⚠️ Kattaroq so'ralsa server uni JIMGINA shu qiymatga tushiradi. Ya'ni
 * "hammasini bitta so'rovda olaman" degan urinish `total` dan kichik
 * natija bilan qaytardi va daraxtning bir qismi hech qanday xatosiz
 * yo'qolardi.
 */
const MAX_PAGE_SIZE = 200

/**
 * Ehtiyot chegarasi — 20 × 200 = 4000 tugun.
 *
 * `OrgNodePage` javadoc'i: bugungi hajm 69 tugun, eng katta prognoz —
 * filial, bo'lim va omborlar qo'shilgandan keyin ham mingdan pastda.
 * Chegara shunga qarab emas, HALQAGA qarshi qo'yilgan: server `total` ni
 * noto'g'ri qaytarsa sikl cheksiz aylanardi.
 */
const MAX_PAGES = 20

export interface OrgTreeLoad {
  /** Butun ko'rinadigan daraxt, `path` bo'yicha tartiblangan */
  nodes: readonly OrgNode[]
  total: number
  /** Chegaraga urildimi — ekranda OCHIQ aytiladi, jimgina kesilmaydi */
  truncated: boolean
}

/**
 * BUTUN daraxtni yassi ro'yxat sifatida oladi.
 *
 * <h2>⚠️ Nega sahifalash EMAS</h2>
 * Daraxtni bitta sahifadan qurib bo'lmaydi: 50-qatordagi tugunning
 * otasi 51-qatorda bo'lishi mumkin va u holda shox ILDIZ bo'lib
 * ko'rinardi — xatosiz, jimgina noto'g'ri. Shuning uchun bu yerda
 * sahifalar KETMA-KET yig'iladi va natija to'liq bo'ladi.
 *
 * Narxi o'lchangan va kichik: bugun 1 ta so'rov (69 tugun), prognozda
 * ham 5 tadan oshmaydi.
 *
 * <h2>⚠️ Ko'rinish doirasi</h2>
 * `SUBTREE` doiradagi foydalanuvchi FAQAT o'z shoxini oladi. Ya'ni
 * natijadagi ba'zi tugunlarning `parentId` si to'plamda BO'LMASLIGI
 * normal holat — `buildOrgTree` ularni ildiz sifatida ko'rsatadi.
 */
export async function fetchOrgTree(signal?: AbortSignal): Promise<OrgTreeLoad> {
  const nodes: OrgNode[] = []
  let total = 0
  let page = 0

  for (; page < MAX_PAGES; page++) {
    const response = await api.get<unknown>('/api/org-nodes', {
      ...(signal ? { signal } : {}),
      params: { page, size: MAX_PAGE_SIZE },
    })
    const parsed = orgNodePageSchema.parse(response.data)
    total = parsed.total
    nodes.push(...parsed.rows.map(toOrgNode))
    if (parsed.rows.length < MAX_PAGE_SIZE || nodes.length >= total) break
  }

  return { nodes, total, truncated: nodes.length < total }
}

export async function fetchOrgNode(id: string): Promise<OrgNode> {
  const response = await api.get<unknown>(`/api/org-nodes/${id}`)
  return toOrgNode(orgNodeSchema.parse(response.data))
}

/**
 * Tugun va butun ostidagilar — bitta GiST qidiruvi, `path` tartibida.
 *
 * Ko'chirish oqibatini ko'rsatishda ishlatiladi: "nechta tugun
 * ta'sirlanadi" degan savolga TAXMIN emas, serverning javobi kerak.
 */
export async function fetchOrgSubtree(id: string): Promise<readonly OrgNode[]> {
  const response = await api.get<unknown>(`/api/org-nodes/${id}/subtree`)
  return z.array(orgNodeSchema).parse(response.data).map(toOrgNode)
}

/**
 * ⚠️ `settlementMode` va `debtLimit` BU YERDA YO'Q — server ularni
 * boshqa qabul qilmaydi (`OrgNodeDraft.java`).
 *
 * Qarz chegarasining YAGONA manbai — `ledger.account.debt_limit`;
 * balansni aynan o'sha ustun to'sadi. Tugundagi nusxani faqat
 * `POST /api/agents/{id}/debt-limit` yozadi va u ikkalasini bitta
 * tranzaksiyada yangilaydi. `org-nodes` yo'li esa ledger'ga tegmasdan
 * faqat nusxani yozardi — ya'ni ekranda bir son, tekshiruvda boshqasi
 * qoladigan YAGONA yo'l shu edi.
 *
 * ⚠️ Ularni bu yerga qaytarish XATO BERMAYDI: server noma'lum JSON
 * maydonini jimgina tashlab yuboradi (`FAIL_ON_UNKNOWN_PROPERTIES`
 * o'chiq), ya'ni forma «saqlandi» deb yozadi, qiymat esa hech qayerga
 * yetib bormaydi.
 *
 * Javobda (`orgNodeSchema`) ikkalasi QOLDI — kartochka ularni
 * ko'rsatadi va bu faqat o'qish.
 */
export interface OrgNodeDraft {
  /** `null` = ildiz tugun */
  parentId: string | null
  code: string
  displayName: string
  /** `org_node_type.code`. ⚠️ Kodda hech qachon satr bilan solishtirilmaydi */
  kind: string
  /** `PUT` to'liq almashtiradi: yuborilmasa rahbar olib tashlanadi */
  managerUserId: string | null
}

export async function createOrgNode(draft: OrgNodeDraft): Promise<OrgNode> {
  const response = await api.post<unknown>('/api/org-nodes', draft)
  return toOrgNode(orgNodeSchema.parse(response.data))
}

/**
 * Tahrirlash.
 *
 * ⚠️ `parentId` TAHRIRDA HAM YUBORILADI va u MAVJUD ota bilan mos
 * kelishi SHART — server mos kelmasa 400 beradi va "ko'chirish uchun
 * POST /{id}/parent" deb aytadi (`OrgNodeDraft` javadoc'i). Shuning
 * uchun chaqiruvchi formadagi qiymatni emas, TUGUNNING o'z otasini
 * beradi: forma otani umuman ko'rsatmaydi.
 */
export async function updateOrgNode(id: string, draft: OrgNodeDraft): Promise<OrgNode> {
  const response = await api.put<unknown>(`/api/org-nodes/${id}`, draft)
  return toOrgNode(orgNodeSchema.parse(response.data))
}

/**
 * Daraxtda KO'CHIRISH — `parentId: null` ildizga chiqaradi.
 *
 * ⚠️ Bu tahrir EMAS, ALOHIDA AMAL va u XAVFLI: butun shoxning `path` i
 * qayta hisoblanadi va abonentlardagi nusxa (`subscriber.reseller_path`)
 * kaskad bilan yangilanadi. Ya'ni ko'chirishdan keyin `SUBTREE`
 * doiradagi operatorlar KO'RADIGAN abonentlar to'plami o'zgaradi.
 *
 * Sikl va `allowed_parents` tekshiruvi BAZADA (`org_node_path_guard`) —
 * paneldagi tanlov ro'yxati faqat QULAYLIK, himoya emas.
 */
export async function moveOrgNode(id: string, parentId: string | null): Promise<OrgNode> {
  const response = await api.post<unknown>(`/api/org-nodes/${id}/parent`, { parentId })
  return toOrgNode(orgNodeSchema.parse(response.data))
}

/**
 * Holatni o'zgartirish — `DELETE` O'RNIDA.
 *
 * Tugun o'chirilmaydi: o'chirish komissiya qoidalarini, oylik limitlarni
 * va (kaskad orqali) butun shoxni olib ketardi.
 */
export async function setOrgNodeState(
  id: string,
  state: OrgNodeState,
): Promise<OperationResult> {
  const response = await api.post<unknown>(`/api/org-nodes/${id}/state`, { state })
  return operationResultSchema.parse(response.data)
}

// --- Tugun TURI -------------------------------------------------------------

const orgNodeTypeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  costCenter: z.boolean(),
  holdsStock: z.boolean(),
  settlementParty: z.boolean(),
  hasEmployees: z.boolean(),
  allowedParents: z.array(z.string()).nullish(),
  /** Tizim turi — o'chirib bo'lmaydi */
  system: z.boolean(),
  /** Shu turdagi tugunlar soni — "o'chirsam nima bo'ladi" savoliga javob */
  usedBy: z.number(),
})

export interface OrgNodeType {
  id: string
  code: string
  name: string
  costCenter: boolean
  holdsStock: boolean
  settlementParty: boolean
  hasEmployees: boolean
  /** Bo'sh ro'yxat = faqat ildiz. ⚠️ Ildiz bo'lish HAR DOIM mumkin */
  allowedParents: readonly string[]
  system: boolean
  usedBy: number
}

function toOrgNodeType(raw: z.infer<typeof orgNodeTypeSchema>): OrgNodeType {
  return { ...raw, allowedParents: raw.allowedParents ?? [] }
}

/** Tur nomi panel tilida; tenant qayta nomlagan tizim turi o'z nomida qoladi. */
export function orgTypeName(type: Pick<OrgNodeType, 'code' | 'name' | 'system'>, t: Translate): string {
  return seededName('orgTypes', type.system ? type.code : null, type.name, t)
}

export async function fetchOrgNodeTypes(): Promise<readonly OrgNodeType[]> {
  const response = await api.get<unknown>('/api/org-node-types')
  return z.array(orgNodeTypeSchema).parse(response.data).map(toOrgNodeType)
}

export interface OrgNodeTypeDraft {
  /** `[A-Z][A-Z0-9_]{1,31}`. ⚠️ Yaratilgandan keyin O'ZGARMAYDI */
  code: string
  name: string
  costCenter: boolean
  holdsStock: boolean
  settlementParty: boolean
  hasEmployees: boolean
  allowedParents: readonly string[]
}

export async function createOrgNodeType(draft: OrgNodeTypeDraft): Promise<OrgNodeType> {
  const response = await api.post<unknown>('/api/org-node-types', draft)
  return toOrgNodeType(orgNodeTypeSchema.parse(response.data))
}

/**
 * ⚠️ `draft.code` MAVJUD kod bilan mos kelishi SHART — server mos
 * kelmasa 400 beradi va sababini aytadi. Kod ikki joyda ishora sifatida
 * yashaydi: `org_node.kind` da va boshqa turlarning `allowedParents`
 * massivida. Shuning uchun formada u faqat KO'RSATILADI, tahrirlanmaydi.
 */
export async function updateOrgNodeType(
  id: string,
  draft: OrgNodeTypeDraft,
): Promise<OrgNodeType> {
  const response = await api.put<unknown>(`/api/org-node-types/${id}`, draft)
  return toOrgNodeType(orgNodeTypeSchema.parse(response.data))
}

/** ⚠️ Tizim turi va ishlatilayotgan tur 409 bilan rad etiladi */
export async function deleteOrgNodeType(id: string): Promise<void> {
  await api.delete<unknown>(`/api/org-node-types/${id}`)
}

// --- Daraxt qurish ----------------------------------------------------------
//
// ⚠️ MANTIQ SHU YERDA EMAS, `shared/lib/tree.ts` DA. Aynan shu uch amal
// agent/reseller daraxtida ham kerak (`AgentMoveModal`), nusxa esa
// ajrashganda JIM buziladi: bitta daraxtda sikl to'siladi, ikkinchisida
// yo'q. Bu yerdagi o'ramlar `OrgNode` tipini va mavjud nomlarni saqlaydi.

export interface OrgTreeNode extends OrgNode {
  children: OrgTreeNode[]
}

export function buildOrgTree(nodes: readonly OrgNode[]): OrgTreeNode[] {
  return buildTree(nodes)
}

export function collectSubtreeIds(nodes: readonly OrgNode[], rootId: string): ReadonlySet<string> {
  return collectSubtreeIdsOf(nodes, rootId)
}

export function ancestorChain(nodes: readonly OrgNode[], nodeId: string | null): readonly OrgNode[] {
  return ancestorChainOf(nodes, nodeId)
}

/**
 * Tugunning TURI — imkoniyatlarni shu yerdan olinadi.
 *
 * ⚠️ Kod bilan solishtirish SHU YERDA VA FAQAT SHU YERDA bo'ladi, va u
 * qaror emas — ma'lumotnomadan qidirish. Qaytgan `null` "tur ro'yxati
 * hali yuklanmagan yoki tur o'chirilgan" degani; chaqiruvchi bunday
 * holatda imkoniyat BOR deb faraz qilmasligi kerak.
 */
export function typeOf(
  types: readonly OrgNodeType[],
  kind: string,
): OrgNodeType | null {
  return types.find((type) => type.code === kind) ?? null
}

/** Tugun turining nomi; tur topilmasa kodning o'zi. */
export function kindName(types: readonly OrgNodeType[], kind: string, t: Translate): string {
  const type = typeOf(types, kind)
  return type ? orgTypeName(type, t) : kind
}

/**
 * Shu tugun turi qaysi turlar ostida tura oladi.
 *
 * ⚠️ Ildiz HAR DOIM mumkin — `allowedParents` dan qat'i nazar
 * (`org_node_path_guard`, 3-qadam va migratsiyadagi izoh: bugungi 69
 * tugunning 65 tasi ildiz). Shuning uchun bu funksiya faqat OTA
 * bo'lishga nomzod turlarni cheklaydi, ildizga chiqarishni emas.
 */
export function canPlaceUnder(
  movedType: OrgNodeType | null,
  parentKind: string,
): boolean {
  // Tur noma'lum bo'lsa hech narsa taqiqlanmaydi: qaror BAZADA, panel
  // faqat yordam beradi. Aks holda ro'yxat yuklanmaganda butun tanlov
  // jimgina bo'sh bo'lib qolardi.
  if (movedType === null) return true
  return movedType.allowedParents.includes(parentKind)
}
